import { useInfiniteQuery } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { ResourceFilter } from "../../domains/TimelineResourceFilter";
import { createError, HTTPErrors } from "../../errors";
import { paths } from "../../lib/api/v1";

// サマリータブのソート済み生徒一覧（カーソルページング）を取得するフック。
// AI 質問統合表示の凍結（005-freeze-ai-question-timeline）に伴い、boron-web からは
// 学習記録軸（resource=studyRecord）のみを渡す。サーバー側は resource 軸切替の受け口を
// 拡張点として維持する（proposal「変更しないもの」）。
const URL = "/api/v1/sections/{section_id}/timeline/students";
type Url = typeof URL;

type TimelineStudentsQuery = paths[Url]["get"]["parameters"]["query"];
type TimelineStudentsResponse =
  paths[Url]["get"]["responses"]["200"]["content"]["application/json"];
// ソート済み生徒一覧の 1 生徒。aiQuestionRequestable / hasLearningManagementService は
// スキーマ上は残るが、凍結中は AI 質問の表示制御には使わず、Total 訴求の判定（COUNT-02 相当）に限る
export type TimelineStudent = TimelineStudentsResponse["students"][0];

type Props = {
  sectionId: string;
  // 表示対象の日付（yyyy-MM-dd・JST）。未指定はサーバー側で当日扱い
  date?: string;
  resourceFilter: ResourceFilter;
  // 既存の生徒絞り込み（job[] / status[] / tag_ids[] / smart_tag[] 等）。URL クエリをそのまま渡す
  studentFilterParams: Record<string, unknown>;
  enabled: boolean;
};

type PageParam = {
  afterCursor: string | null;
};

export const useFetchTimelineStudents = ({
  sectionId,
  date,
  resourceFilter,
  studentFilterParams,
  enabled,
}: Props) => {
  const { data, ...result } = useInfiniteQuery<
    TimelineStudentsResponse,
    HTTPErrors
  >({
    // フィルタ条件・日付ごとにキャッシュを分離する（条件間で使い回さない）
    queryKey: [URL, sectionId, date, resourceFilter, studentFilterParams],
    queryFn: ({ pageParam }) =>
      fetchTimelineStudents({
        sectionId,
        date,
        resourceFilter,
        studentFilterParams,
        pageParam: pageParam as PageParam,
      }),
    initialPageParam: { afterCursor: null } satisfies PageParam,
    getNextPageParam: (lastPage): PageParam | undefined => {
      if (lastPage.meta.nextCursor !== null) {
        return { afterCursor: lastPage.meta.nextCursor };
      }
      return undefined;
    },
    enabled,
  });

  return {
    students: data?.pages.flatMap((page) => page.students),
    // 取得済みページ数。次ページ取得トリガーの <div> の key に使う（WEB-1947）。
    // useOnScrollContentBottom は監視対象の要素 1 つにつき 1 回だけコールバックを呼ぶ。
    // key を変えて要素を置き換えると、新しい要素として再びコールバックが呼ばれ、次ページを取得できる。
    // ページ数はフェッチ成功ごとに必ず 1 増えるので、生徒数が変わらないページでも key が確実に変わる。
    loadedPageCount: data?.pages.length ?? 0,
    ...result,
  };
};

const fetchTimelineStudents = async ({
  sectionId,
  date,
  resourceFilter,
  studentFilterParams,
  pageParam,
}: Omit<Props, "enabled"> & {
  pageParam: PageParam;
}): Promise<TimelineStudentsResponse> => {
  // 生徒絞り込みは URL クエリをそのまま渡すため、生成型へキャストして合成する。
  // 凍結中は resource=studyRecord に固定する（AI 質問軸への切替は行わない）。
  const query = {
    ...studentFilterParams,
    date,
    resource: "studyRecord",
    resource_filter: resourceFilter,
    after_cursor: pageParam.afterCursor ?? undefined,
  } as TimelineStudentsQuery;

  const { data, response } = await boronClient.GET(URL, {
    params: {
      path: { section_id: sectionId },
      query,
    },
  });

  if (response.ok && data) {
    return data;
  }

  throw await createError(response);
};
